// Mercapoli - Header del Panel de Comercio con Logout
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft, LogOut, Menu, X, Store, 
  BarChart3, ShoppingBag, Tag, Star, FileText, Wallet, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { useAuth } from '../../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const menuItems = [
  { icon: BarChart3, label: 'Dashboard', path: '/merchant' },
  { icon: ShoppingBag, label: 'Productos', path: '/merchant/productos' },
  { icon: Tag, label: 'Promociones', path: '/merchant/promociones' },
  { icon: Star, label: 'Reviews', path: '/merchant/reviews' },
  { icon: FileText, label: 'Ventas', path: '/merchant/ventas' },
  { icon: Wallet, label: 'Liquidaciones', path: '/merchant/liquidaciones' },
  { icon: Settings, label: 'Configuración', path: '/merchant/configuracion' },
];

const MerchantHeader = ({ 
  title, 
  showBack = true, 
  backPath = '/merchant',
  rightContent = null 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Left side */}
          <div className="flex items-center gap-3">
            {showBack && (
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
            <h1 className="font-heading font-bold">{title}</h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {rightContent}
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Store className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.nombre || 'Mi Comercio'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/merchant" className="cursor-pointer">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <img src={LOGO_URL} alt="Mercapoli" className="h-8 w-8" />
                    Panel Comercio
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-6 space-y-1">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSheetOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        location.pathname === item.path 
                          ? 'bg-brand-primary text-white' 
                          : 'hover:bg-muted'
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </nav>
                <div className="absolute bottom-6 left-4 right-4">
                  <div className="flex items-center gap-3 mb-3 p-3 bg-muted rounded-lg">
                    <Store className="h-6 w-6 text-brand-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user?.nombre || 'Mi Comercio'}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full text-red-600 border-red-200 hover:bg-red-50" 
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Cerrar Sesión
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MerchantHeader;
